/**
 * sdkRealtimeProvider — realtime backend for the SDK's
 * `workspaceProject.realtime.*` API surface.
 *
 * Wired into the SDK at boot via
 * `(await sdk.getService('workspaceProject')).setRealtimeProvider(realtimeProvider)`.
 * Once installed, every consumer using
 *   `sdk.execute('workspaceProject.realtime.<entity>', 'subscribe', filter, cb)`
 * gets live events through the transport this provider selects per op.
 *
 * ── Live transports ──────────────────────────────────────────────────────────
 *   'meet'          → server SSE stream (GET /core/meet/stream) via the SDK's
 *                     `workspaceProject.realtime.subscribeMeetSse` (_openMeetSse).
 *                     Emits the (kind, payload) + snake_case `{eventType,new,old}`
 *                     envelope meet consumers expect.
 *   'presence'      → SDK /user-socket presence registry (sdk.subscribePresence,
 *                     _openPresenceSocket).
 *   'notifications' → SDK /user-socket: the server emits `notification-created`
 *                     to the recipient's `user:<id>` room at the notifications
 *                     store write choke point, reframed here into the
 *                     `{eventType:'INSERT', new, old}` envelope
 *                     (_openNotificationsSocket).
 *
 * ── Retired backend ─────────────────────────────────────────────────────────
 * Supabase (the governance project) is fully retired. It formerly served
 * chat.messages / chat.channels / chat.mentions and the presence channel via
 * postgres_changes; those ops no longer have a backend, so the provider no
 * longer handles them: they fall through (no return) and the SDK's
 * `_realtimeSubscribe` coerces the absent unsubscribe to a no-op. Chat
 * realtime is owned by the /chat page's own `subscribeRealtime.js` (server
 * SSE), never this provider.
 *
 * Provider contract (from WorkspaceProjectService):
 *   ({ op, filter, callback }) → unsubscribe fn
 */

import { getSDK } from './sdk.js'

// FA204 / frank trap — _openPresenceSocket/_openNotificationsSocket/
// _openMeetSse/_normalizeMeetTable/_MEET_TABLE_ALIASES used to live at
// module scope and get referenced bare from this file's one export below;
// this is that export's own self-contained copy of all five.
export const realtimeProvider = ({ op, filter = {}, callback }) => {
  if (typeof callback !== 'function') return () => {}

  // presence: socket transport (the cutover end-state). Routes through the
  // SDK's /user-socket presence (sdk.subscribePresence) and reframes its
  // `presence:sync` state into the consumer callback('sync', { state }).
  // `state` is byte-compatible with Supabase channel.presenceState(), so
  // presence.js needs no change. Returns the SDK subscription's unsub
  // (disconnects the socket → server drops the user).
  const openPresenceSocket = ({ filter: f, callback: cb }) => {
    const scope = f.scope || 'symbols'
    try {
      const sdk = getSDK()
      if (!sdk || typeof sdk.subscribePresence !== 'function') return () => {}
      const sub = sdk.subscribePresence(
        { scope, userKey: f.userKey, meta: f.meta || {} },
        (state) => {
          try {
            cb('sync', { state })
          } catch (_) {}
        }
      )
      // Through the init-await proxy the subscription arrives as a PROMISE of
      // the unsub; returned bare, the SDK coerced it to a no-op and an
      // unsubscribe never disconnected the socket.
      return () => {
        Promise.resolve(sub)
          .then((unsub) => {
            if (typeof unsub === 'function') unsub()
          })
          .catch(() => {})
      }
    } catch (err) {
      console.warn('[realtimeProvider] presence socket subscribe failed:', err?.message || err)
      return () => {}
    }
  }

  // notifications: user-socket transport (the cutover end-state). The server
  // emits `notification-created` (the wire-shaped row, same shape the REST
  // list returns) to the recipient's `user:<id>` room from the
  // notifications-store write choke point (server
  // src/domains/notifications/store/index.js) — re-frame it into the
  // postgres_changes INSERT envelope so the consumer
  // (subscribeNotifications.js reads payload.new) is byte-unchanged. The
  // room is per-recipient, so no client-side user_email filter is needed —
  // the server only emits to the notification's recipient.
  const openNotificationsSocket = ({ callback: cb }) => {
    try {
      const sdk = getSDK()
      if (!sdk || typeof sdk.subscribeUserEvents !== 'function') return () => {}
      return sdk.subscribeUserEvents({
        'notification-created': (row) => {
          if (!row) return
          try {
            cb('INSERT', { eventType: 'INSERT', new: row, old: null })
          } catch (_) {}
        }
      })
    } catch (err) {
      console.warn('[realtimeProvider] notifications socket subscribe failed:', err?.message || err)
      return () => {}
    }
  }

  // ── meet realtime: table-name normalization ───────────────────────────
  // Accept BOTH short keys (`rooms`, `participants`, `waiting`,
  // `transcripts`, `analyses`) and the full table names. Several call sites
  // pass the full names; without this every subscription fell through (the
  // old captions bug).
  const MEET_TABLE_ALIASES = {
    meet_rooms: 'rooms',
    meet_participants: 'participants',
    meet_waiting_room: 'waiting',
    meet_transcripts: 'transcripts',
    meet_transcript_analyses: 'analyses'
  }
  const normalizeMeetTable = (t) => MEET_TABLE_ALIASES[t] || t

  // ── meet realtime: server-SSE transport ────────────────────────────────
  // Delegates to the SDK's `workspaceProject.realtime.subscribeMeetSse`,
  // which owns the EventSource client + the SSE→(kind,payload) re-framing.
  // The SDK preserves the SAME (kind, payload) + snake_case
  // `{eventType,new,old}` contract, so the consumer is unchanged.
  //
  // getService('workspaceProject') is a promise on the shell's SDK proxy
  // (and may also be asked before the SDK is ready). Resolve it and return a
  // deferred unsub — the same pattern subscribeMeetRealtime.js uses.
  const openMeetSse = ({ filter: f, callback: cb }) => {
    let resolvedUnsub = null
    let cancelled = false

    const tables =
      Array.isArray(f.tables) && f.tables.length ? [...f.tables].map(normalizeMeetTable) : undefined

    // getService answers a PROMISE through the shell's init-await proxy
    // (sdkClient.js wrapWithInitAwait). Read synchronously, `wp` was that
    // promise, `wp.realtime` was undefined and no stream ever opened.
    const open = (wp) => {
      if (cancelled) return
      const sub = wp?.realtime?.subscribeMeetSse
      if (typeof sub !== 'function') {
        console.warn('[meet-rt] SSE subscribe unavailable — no realtime.subscribeMeetSse')
        return
      }
      const unsub = sub.call(
        wp.realtime,
        {
          roomId: f.roomId || undefined,
          workspaceId: f.workspaceId || undefined,
          tables
        },
        cb
      )
      resolvedUnsub = typeof unsub === 'function' ? unsub : null
    }

    try {
      Promise.resolve(getSDK()?.getService?.('workspaceProject'))
        .then(open)
        .catch((err) => {
          console.warn('[meet-rt] SSE subscribe failed:', err?.message || err)
        })
    } catch (err) {
      console.warn('[meet-rt] SSE subscribe failed:', err?.message || err)
    }

    return () => {
      cancelled = true
      if (typeof resolvedUnsub === 'function') {
        try {
          resolvedUnsub()
        } catch (_) {}
      }
      resolvedUnsub = null
    }
  }

  // meet → server SSE.
  if (op === 'meet') return openMeetSse({ filter, callback })

  // presence → SDK /user-socket presence registry.
  if (op === 'presence') return openPresenceSocket({ filter, callback })

  // notifications → SDK /user-socket (`notification-created` emitted server-side
  // at the store write path).
  if (op === 'notifications') return openNotificationsSocket({ callback })

  // Any other op has no realtime backend — the chat/presence ops formerly
  // served by the retired governance Supabase project. Fall through: the
  // SDK's `_realtimeSubscribe` normalizes the absent unsubscribe return to a
  // no-op.
}
